/**
 * AddEmployeeDialog — used by the NECS Admin "Employees" tab.
 * Step 1: choose Admin Agent or Staff Agent.
 * Step 2: fill structured fields aligned to the registration pages.
 *   - Admin path  → Department, Position, State (shift auto-locked to Mon-Fri 8:30 AM – 4:30 PM)
 *   - Staff path  → Role (Valet/Cleaner), Position, Region, State
 * Auto-generates the structured badge via /api/access-requests/generate-badge.
 * Posts to /api/company/employees.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Briefcase, UserPlus, ShieldCheck, Truck, RefreshCw } from 'lucide-react';
import { DFW_REGIONS, getRegionByCode, getRegionsForBigCity } from '../utils/dfwRegions';
import { CleanerQualificationFields } from './admin/CleanerQualificationFields';
import { getAdminViewAs } from '../lib/adminScopeTransport';
import { CORPORATE_HOURS, WAREHOUSE_SHIFT_ORDER, DEFAULT_WAREHOUSE_SHIFT, warehouseShiftLabel } from '../utils/corporateSchedules';
import { SharedPayPreview, WAREHOUSE_PAY_ROLES } from './admin/SharedPayPreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mirrors NECSAccessRequestForm.jsx — keep in sync if either source list changes.
const ADMIN_DEPARTMENTS = [
  'Executive / C-Suite', 'Operations', 'Finance & Accounting', 'Human Resources',
  'Information Technology', 'Legal & Compliance', 'Sales & Business Development',
  'Marketing & Communications', 'Customer Service', 'Procurement & Supply Chain',
  'Safety & Risk Management', 'Training & Development',
];
// ── Position catalogs (Feb 2026 CEO directive) ──────────────────────
// Iter-97 · Recruiter + Trainer removed per spec — those roles no
// longer exist in the platform. Corporate ladder + field ladder are
// slimmed to the active-role list only.
const ADMIN_POSITIONS = [
  'CEO', 'Director', 'Assistant Director',
  'Manager', 'Supervisor', 'Lead',
  'Admin Agent',
];
const STAFF_POSITIONS = [
  'Regional Manager', 'Regional Supervisor',
  'Crew Leader', 'Site Agent',
];

// Big City tab list — matches the metro switcher used across Routes
// & Regions and every Operations drill-down.
const BIG_CITIES = ['Dallas', 'Houston', 'Austin', 'San Antonio', 'Tyler'];
import { STAFF_SHIFTS, STAFF_SHIFT_ORDER, shiftLabel, DEFAULT_STAFF_SHIFT } from '../utils/staffShifts';

const STAFF_ROLE_TYPES = ['Valet', 'Cleaner'];
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];
const STAFF_ROLE_TO_USER_ROLE = {
  // Iter-100 · testing agent fix — Regional Manager maps to 'manager' so
  // the backend badge builder receives RGM (role_code) instead of SUP.
  'Regional Manager': 'manager',
  'Regional Supervisor': 'supervisor',
  'Crew Leader': 'crew_leader',
  'Site Agent': 'agent',
};

const defaultAdminForm = {
  first_name: '', middle_name: '', last_name: '',
  name: '', email: '', phone: '',
  department: ADMIN_DEPARTMENTS[0],
  position: ADMIN_POSITIONS[0],
  corporate_team: 'office',
  assigned_shift: 'admin_office',
  big_city: BIG_CITIES[0], // Dallas by default
  state: 'TX',
  badge_number: '',
};
const defaultStaffForm = {
  first_name: '', middle_name: '', last_name: '',
  name: '', email: '', phone: '',
  role_type: STAFF_ROLE_TYPES[0],
  position: STAFF_POSITIONS[3],   // Site Agent default
  big_city: BIG_CITIES[0], // Dallas by default
  region: '',
  state: 'TX',
  assigned_shift: DEFAULT_STAFF_SHIFT,
  badge_number: '',
  primary_cleaner_role: '',
  cleaner_qualifications: [],
};

// Build the canonical `name` field from First / Middle / Last so the
// existing backend contract keeps working unchanged.
const composeName = (f) =>
  [f.first_name, f.middle_name, f.last_name]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(' ');

export default function AddEmployeeDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('choose');                 // 'choose' | 'admin' | 'staff'
  const [adminForm, setAdminForm] = useState(defaultAdminForm);
  const [staffForm, setStaffForm] = useState(() => ({ ...defaultStaffForm, role_type: getAdminViewAs() === 'cleaners' ? 'Cleaner' : 'Valet' }));
  const [badgeLoading, setBadgeLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // -- helpers ---------------------------------------------------------------
  const reset = () => {
    setStep('choose');
    setAdminForm(defaultAdminForm);
    setStaffForm({ ...defaultStaffForm, role_type: getAdminViewAs() === 'cleaners' ? 'Cleaner' : 'Valet' });
  };

  const refreshAdminBadge = async (snap) => {
    setBadgeLoading(true);
    try {
      const qs = new URLSearchParams({
        kind: 'necs_admin',
        department: snap.department,
        position: snap.position,
        big_city: snap.big_city,
        state: snap.state,
      });
      const res = await fetch(`${BACKEND_URL}/api/access-requests/generate-badge?${qs}`);
      const data = await res.json();
      if (data?.badge_number) setAdminForm((f) => ({ ...f, badge_number: data.badge_number }));
    } catch (_) { /* silent */ }
    finally { setBadgeLoading(false); }
  };

  const refreshStaffBadge = async (snap) => {
    setBadgeLoading(true);
    try {
      const qs = new URLSearchParams({
        kind: 'necs_staff',
        role: snap.role_type,
        position: snap.position,
        big_city: snap.big_city,
        region: snap.region || '',
        state: snap.state,
      });
      const res = await fetch(`${BACKEND_URL}/api/access-requests/generate-badge?${qs}`);
      const data = await res.json();
      if (data?.badge_number) setStaffForm((f) => ({ ...f, badge_number: data.badge_number }));
    } catch (_) { /* silent */ }
    finally { setBadgeLoading(false); }
  };

  // Pre-fetch the badge whenever the user enters an admin/staff form for the first time
  useEffect(() => {
    if (step === 'admin' && !adminForm.badge_number) refreshAdminBadge(adminForm);
    if (step === 'staff' && !staffForm.badge_number) refreshStaffBadge(staffForm);
   
  }, [step]);

  // -- submit ----------------------------------------------------------------
  const submitAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fullName = composeName(adminForm) || adminForm.name;
      const res = await fetch(`${BACKEND_URL}/api/company/employees`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_kind: 'admin',
          role: 'admin',
          email: adminForm.email,
          name: fullName,
          first_name: adminForm.first_name,
          middle_name: adminForm.middle_name,
          last_name: adminForm.last_name,
          phone: adminForm.phone,
          department: adminForm.department,
          position: adminForm.position,
          corporate_team: adminForm.corporate_team,
          big_city: adminForm.big_city,
          state: adminForm.state,
          assigned_shift: adminForm.assigned_shift,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to create admin employee');
      toast.success(`Admin ${data.employee.name} created — badge ${data.employee.badge_number}`);
      if (data.employee.temp_password) toast.info(`Temporary password: ${data.employee.temp_password}`, { duration: 10000 });
      setOpen(false); reset(); onCreated && onCreated(data.employee);
    } catch (err) {
      toast.error(err.message);
    } finally { setSubmitting(false); }
  };

  const submitStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.region) {
      toast.error('Region is required for all field roles (Supervisor, Crew Leader, Site Agent)');
      return;
    }
    if (!staffForm.city) {
      toast.error('City is required — pick a city within the selected region');
      return;
    }
    setSubmitting(true);
    try {
      const userRole = STAFF_ROLE_TO_USER_ROLE[staffForm.position] || 'agent';
      const fullName = composeName(staffForm) || staffForm.name;
      const res = await fetch(`${BACKEND_URL}/api/company/employees`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_kind: 'staff',
          role: userRole,
          email: staffForm.email,
          name: fullName,
          first_name: staffForm.first_name,
          middle_name: staffForm.middle_name,
          last_name: staffForm.last_name,
          phone: staffForm.phone,
          role_type: staffForm.role_type,
          primary_service: staffForm.role_type === 'Cleaner' ? 'cleaning' : 'valet',
          ...(staffForm.role_type === 'Cleaner' ? { primary_cleaner_role: staffForm.primary_cleaner_role || null, cleaner_qualifications: staffForm.cleaner_qualifications } : {}),
          position: staffForm.position,
          big_city: staffForm.big_city,
          region: staffForm.region,
          city: staffForm.city,
          state: staffForm.state,
          assigned_shift: staffForm.assigned_shift,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to create staff employee');
      toast.success(`Staff ${data.employee.name} created — badge ${data.employee.badge_number}`);
      if (data.employee.temp_password) toast.info(`Temporary password: ${data.employee.temp_password}`, { duration: 10000 });
      setOpen(false); reset(); onCreated && onCreated(data.employee);
    } catch (err) {
      toast.error(err.message);
    } finally { setSubmitting(false); }
  };

  // -- render ----------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          className="bg-[#C9A962] text-black hover:opacity-90 font-bold"
          data-testid="add-employee-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add Employee
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 text-white border-zinc-800 w-[calc(100%-2rem)] max-w-2xl min-w-0 grid-cols-1 p-4 sm:p-6 max-h-[90vh] overflow-y-auto" data-testid="add-employee-dialog">
        <DialogHeader>
          <DialogTitle data-testid="add-employee-title">
            {step === 'choose' && 'Add Employee — choose type'}
            {step === 'admin' && 'New Corporate Employee'}
            {step === 'staff' && 'New Field Staff (valet / cleaner)'}
          </DialogTitle>
          <DialogDescription className="text-zinc-400" data-testid="add-employee-description">{step === 'admin' ? 'Corporate office and Procurement warehouse personnel.' : 'Employee identity, department and assigned schedule.'}</DialogDescription>
        </DialogHeader>

        {step === 'choose' && (
          <div className="grid sm:grid-cols-2 gap-4 mt-2" data-testid="add-employee-choose">
            <ChoiceCard
              testid="choose-admin"
              icon={ShieldCheck}
              title="NECS Corporate"
              desc="Office & Warehouse · Department · Position · Assigned schedule."
              onClick={() => setStep('admin')}
            />
            <ChoiceCard
              testid="choose-staff"
              icon={Truck}
              title="Staff Agent"
              desc="Field crew. Valet or Cleaner. Badge based on Role · Position · Region · State."
              onClick={() => setStep('staff')}
            />
          </div>
        )}

        {step === 'admin' && (
          <form onSubmit={submitAdmin} className="space-y-4 min-w-0" data-testid="admin-employee-form">
            <PersonFields f={adminForm} setF={setAdminForm} idPrefix="adm" />

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Department *">
                <SelectInput
                  testid="adm-department"
                  value={adminForm.department}
                  options={ADMIN_DEPARTMENTS}
                  onChange={(v) => setAdminForm((f) => ({ ...f, department: v, corporate_team:'office', assigned_shift:'admin_office', position:f.position==='Warehouse Agent'?'Admin Agent':f.position, badge_number: '' }))}
                />
              </Field>
              <Field label="Position *">
                <SelectInput
                  testid="adm-position"
                  value={adminForm.position}
                  options={adminForm.corporate_team==='warehouse'?['Manager','Supervisor','Lead','Warehouse Agent']:ADMIN_POSITIONS}
                  onChange={(v) => setAdminForm((f) => ({ ...f, position: v, badge_number: '' }))}
                />
              </Field>
            </div>

            {adminForm.department==='Procurement & Supply Chain'&&<div className="space-y-2"><Field label="Corporate team"><SelectInput testid="adm-corporate-team" value={adminForm.corporate_team} options={['office','warehouse']} renderOption={v=>v==='warehouse'?'Warehouse':'Office'} onChange={v=>setAdminForm(f=>({...f,corporate_team:v,assigned_shift:v==='warehouse'?DEFAULT_WAREHOUSE_SHIFT:'admin_office',position:v==='warehouse'?'Warehouse Agent':'Admin Agent',badge_number:''}))}/></Field>{adminForm.corporate_team==='warehouse'&&<p className="text-xs text-zinc-300" data-testid="adm-warehouse-duties">Warehouse · Stock, deliveries, audits and supplier prices. No HR, payroll or Finance approval access.</p>}</div>}

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Big City *">
                <SelectInput
                  testid="adm-bigcity"
                  value={adminForm.big_city}
                  options={BIG_CITIES}
                  onChange={(v) => setAdminForm((f) => ({ ...f, big_city: v, badge_number: '' }))}
                />
                <p className="text-[11px] text-zinc-500 mt-1">NECS launch metro or expansion market.</p>
              </Field>
              <Field label="State *">
                <SelectInput
                  testid="adm-state"
                  value={adminForm.state}
                  options={US_STATES}
                  onChange={(v) => setAdminForm((f) => ({ ...f, state: v, badge_number: '' }))}
                />
              </Field>
            </div>
              <Field label={adminForm.corporate_team === 'warehouse' ? 'Warehouse schedule *' : 'Corporate office hours'}>
                {adminForm.corporate_team === 'warehouse' ? <SelectInput
                  testid="adm-warehouse-shift"
                  value={adminForm.assigned_shift}
                  options={WAREHOUSE_SHIFT_ORDER}
                  renderOption={warehouseShiftLabel}
                  onChange={value => setAdminForm(f => ({ ...f, assigned_shift: value }))}
                /> : <Input
                  value={CORPORATE_HOURS}
                  readOnly
                  className="bg-zinc-800 border-zinc-700 text-white cursor-not-allowed"
                  data-testid="adm-shift"
                />}
                <p className="text-[11px] text-zinc-400 mt-1" data-testid="adm-schedule-policy">{adminForm.corporate_team === 'warehouse' ? 'Monday–Saturday · Overnight shifts finish the following morning.' : 'Office schedule · Monday–Friday.'}</p>
              </Field>

            <BadgePreview
              loading={badgeLoading}
              value={adminForm.badge_number}
              format="NECS-A-[DEPT]-[POSITION]-[STATE]-[SEQ]"
              onRefresh={() => refreshAdminBadge(adminForm)}
              testid="adm-badge"
            />

            {adminForm.corporate_team === 'warehouse' && <SharedPayPreview role={WAREHOUSE_PAY_ROLES[adminForm.position]} prefix="adm-warehouse"/>}
            <StepFooter onBack={() => setStep('choose')} submitLabel={submitting ? 'Creating…' : 'Create Corporate Employee'} submitting={submitting} testid="adm-submit" />
          </form>
        )}

        {step === 'staff' && (
          <form onSubmit={submitStaff} className="space-y-4" data-testid="staff-employee-form">
            <PersonFields f={staffForm} setF={setStaffForm} idPrefix="sta" />

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Field Role *">
                <SelectInput
                  testid="sta-role-type"
                  value={staffForm.role_type}
                  options={STAFF_ROLE_TYPES}
                  onChange={(v) => setStaffForm((f) => ({ ...f, role_type: v, badge_number: '' }))}
                />
              </Field>
              <Field label="Position *">
                <SelectInput
                  testid="sta-position"
                  value={staffForm.position}
                  options={STAFF_POSITIONS}
                  onChange={(v) => setStaffForm((f) => ({ ...f, position: v, badge_number: '' }))}
                />
              </Field>
            </div>

            {/* Big City cascade — scopes Region + City below.  Big
                City = Dallas is the only metro with seeded regions
                today; others are placeholders for expansion. */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Big City *">
                <SelectInput
                  testid="sta-bigcity"
                  value={staffForm.big_city}
                  options={BIG_CITIES}
                  onChange={(v) => setStaffForm((f) => ({ ...f, big_city: v, region: '', city: '', badge_number: '' }))}
                />
              </Field>
              <Field label="State *">
                <SelectInput
                  testid="sta-state"
                  value={staffForm.state}
                  options={US_STATES}
                  onChange={(v) => setStaffForm((f) => ({ ...f, state: v, badge_number: '' }))}
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Region *">
                <SelectInput
                  testid="sta-region"
                  value={staffForm.region}
                  options={['', ...getRegionsForBigCity(staffForm.big_city).map((r) => r.code)]}
                  renderOption={(code) => {
                    if (!code) return '— Select a region —';
                    const r = getRegionByCode(code);
                    return r ? `${r.code} · ${r.name}` : code;
                  }}
                  onChange={(v) =>
                    setStaffForm((f) => ({ ...f, region: v, city: '', badge_number: '' }))
                  }
                />
              </Field>
              <Field label="City *">
                <select
                  value={staffForm.city}
                  onChange={(e) => setStaffForm((f) => ({ ...f, city: e.target.value }))}
                  disabled={!staffForm.region}
                  className="w-full h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962] disabled:opacity-60"
                  data-testid="sta-city"
                >
                  <option value="">{staffForm.region ? '— Select a city —' : 'Pick a region first'}</option>
                  {(getRegionByCode(staffForm.region)?.cities || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Assigned Shift *">
              <SelectInput
                testid="sta-shift"
                value={staffForm.assigned_shift}
                options={STAFF_SHIFT_ORDER}
                renderOption={(code) => shiftLabel(code)}
                onChange={(v) => setStaffForm((f) => ({ ...f, assigned_shift: v }))}
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Overnight coverage includes 7:30 PM–4:30 AM the following day.
              </p>
            </Field>

            <BadgePreview
              loading={badgeLoading}
              value={staffForm.badge_number}
              format="NECS-S-[ROLE]-[POSITION]-[REGION]-[STATE]-[SEQ]"
              onRefresh={() => refreshStaffBadge(staffForm)}
              testid="sta-badge"
            />

            {/* Iter-180 · Phase 6 — Live sidebar-style regional badge
                preview. Updates instantly on any field change; matches
                the exact string that will render in the agent's sidebar. */}
            <RegionalBadgePreview
              roleLine={staffForm.role_type}
              state={staffForm.state}
              region={staffForm.region}
              city={staffForm.city}
            />

            {staffForm.role_type === 'Valet' && <SharedPayPreview role={STAFF_ROLE_TO_USER_ROLE[staffForm.position]} prefix="sta-valet"/>}
            <StepFooter onBack={() => setStep('choose')} submitLabel={submitting ? 'Creating…' : 'Create Staff Agent'} submitting={submitting} testid="sta-submit" />
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Iter-180 · Phase 6 — Live regional badge preview matched to the
// sidebar identity styling.
function RegionalBadgePreview({ roleLine, state, region, city }) {
  const [badge, setBadge] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setError(null);
    if (!roleLine || !state || !region || !city) { setBadge(null); return; }
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/staff/regional-badge/preview`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: 'preview', email: 'preview@necsdemo.com',
            role_line: (roleLine || '').toLowerCase() === 'cleaner' ? 'cleaners' : 'valet',
            state, region_code: region, city,
          }),
          signal: ctrl.signal,
        });
        if (r.ok) {
          const data = await r.json();
          setBadge(data.badge);
        } else {
          const err = await r.json().catch(() => ({}));
          setBadge(null);
          setError(err.detail || 'Preview unavailable');
        }
      } catch (_) { /* aborted */ }
    })();
    return () => ctrl.abort();
  }, [roleLine, state, region, city]);
  return (
    <div className="rounded-md border border-zinc-700 bg-zinc-900 p-3"
         data-testid="regional-badge-preview">
      <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-2">
        Sidebar badge preview
      </p>
      {badge ? (
        <div
          className="mx-auto max-w-[220px] rounded-md px-2 py-1.5 text-[11px] font-bold uppercase tracking-widest text-center"
          style={{
            backgroundColor: '#0a0a0a', color: '#C9A962',
            border: '1px solid #C9A962',
            fontFamily: 'Barlow Condensed, sans-serif',
            letterSpacing: '0.12em',
          }}
          data-testid="regional-badge-preview-value"
        >
          {badge}
        </div>
      ) : (
        <p className="text-xs italic text-zinc-500"
           data-testid="regional-badge-preview-empty">
          {error || 'Fill role, state, region and city to preview.'}
        </p>
      )}
    </div>
  );
}

// ─── sub-components ─────────────────────────────────────────────────────────
function ChoiceCard({ testid, icon: Icon, title, desc, onClick }) {
  return (
    <Card
      onClick={onClick}
      className="p-5 cursor-pointer bg-zinc-800 border-zinc-700 hover:border-[#C9A962] transition"
      data-testid={testid}
    >
      <Icon className="h-8 w-8 mb-3" style={{ color: '#C9A962' }} />
      <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
        {title}
      </h3>
      <p className="text-xs text-zinc-400 leading-relaxed">{desc}</p>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <Label className="text-white">{label}</Label>
      {children}
    </div>
  );
}

function SelectInput({ testid, value, options, onChange, renderOption }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full min-w-0 h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]"
      data-testid={testid}
    >
      {options.map((o) => <option key={o} value={o}>{renderOption ? renderOption(o) : o}</option>)}
    </select>
  );
}

function PersonFields({ f, setF, idPrefix }) {
  return (
    <>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="First Name *">
          <Input
            required
            value={f.first_name || ''}
            onChange={(e) => setF((s) => ({ ...s, first_name: e.target.value }))}
            className="bg-zinc-800 border-zinc-700 text-white"
            data-testid={`${idPrefix}-first-name`}
          />
        </Field>
        <Field label="Middle Name">
          <Input
            value={f.middle_name || ''}
            onChange={(e) => setF((s) => ({ ...s, middle_name: e.target.value }))}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder="Optional"
            data-testid={`${idPrefix}-middle-name`}
          />
        </Field>
        <Field label="Last Name *">
          <Input
            required
            value={f.last_name || ''}
            onChange={(e) => setF((s) => ({ ...s, last_name: e.target.value }))}
            className="bg-zinc-800 border-zinc-700 text-white"
            data-testid={`${idPrefix}-last-name`}
          />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Email *">
          <Input required type="email" value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`${idPrefix}-email`} />
        </Field>
        <Field label="Phone">
          <Input value={f.phone} onChange={(e) => setF((s) => ({ ...s, phone: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`${idPrefix}-phone`} />
        </Field>
      </div>
    </>
  );
}

function BadgePreview({ loading, value, format, onRefresh, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="text-white flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> Badge (auto-assigned)</Label>
      <div className="flex gap-2">
        <Input
          readOnly
          value={loading ? 'Generating…' : value}
          className="bg-zinc-800 border-zinc-700 text-[#C9A962] font-mono cursor-not-allowed flex-1 min-w-0"
          data-testid={`${testid}-value`}
        />
        <Button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="bg-zinc-700 hover:bg-zinc-600 px-3"
          data-testid={`${testid}-refresh`}
          title="Regenerate badge using the current selections"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-[11px] text-zinc-500 mt-1">Format: {format}</p>
    </div>
  );
}

function StepFooter({ onBack, submitLabel, submitting, testid }) {
  return (
    <div className="flex flex-col-reverse sm:flex-row gap-3 items-stretch sm:items-center justify-between pt-2">
      <Button type="button" variant="ghost" className="text-zinc-300 hover:bg-zinc-800 whitespace-normal" onClick={onBack} data-testid={`${testid}-back`}>
        ← Choose another type
      </Button>
      <Button type="submit" disabled={submitting} className="bg-[#C9A962] text-black hover:opacity-90 font-bold whitespace-normal" data-testid={testid}>
        {submitLabel}
      </Button>
    </div>
  );
}
